import { createSelector } from 'reselect';
import { getStateAtNamespaceKey } from 'redux/get-state-at-namespace-key';
import { BasketBuilderExperienceState, BasketBuilderExperienceSuccessState } from '../types';
import { STATUS_ELIGIBLE, STATUS_INELIGIBLE } from '../constants';

const isEligibilitySuccessfullyLoaded = (
  state: BasketBuilderExperienceState,
): state is BasketBuilderExperienceSuccessState => 'eligibility' in state;

const isSuggestionsSuccessfullyLoaded = (
  state: BasketBuilderExperienceState,
): state is BasketBuilderExperienceSuccessState => 'suggestions' in state;

const getBasketBuilder = (state: WtrState) => getStateAtNamespaceKey(state, 'basketBuilder');

const getEligibility = createSelector(getBasketBuilder, basketBuilder =>
  isEligibilitySuccessfullyLoaded(basketBuilder) ? basketBuilder.eligibility : null,
);

export const isEligible = createSelector(
  getEligibility,
  eligibility => eligibility?.status === STATUS_ELIGIBLE,
);

export const getIneligibleReason = createSelector(getEligibility, eligibility =>
  eligibility?.status === STATUS_INELIGIBLE ? eligibility.reason : null,
);

const EMPTY_SUGGESTIONS_ARRAY: never[] = [];

export const getSuggestions = createSelector([getBasketBuilder], basketBuilder =>
  isSuggestionsSuccessfullyLoaded(basketBuilder)
    ? basketBuilder.suggestions
    : EMPTY_SUGGESTIONS_ARRAY,
);

export const getSuggestionsLoading = createSelector(
  getBasketBuilder,
  basketBuilder => basketBuilder.loading ?? true,
);

export const getSuggestionsLoadingMore = createSelector(
  getBasketBuilder,
  basketBuilder => basketBuilder.loadingMore ?? false,
);

export const getSuggestionsHasNextPage = createSelector(getBasketBuilder, basketBuilder =>
  isSuggestionsSuccessfullyLoaded(basketBuilder) ? basketBuilder.hasNextPage : false,
);

export const getSuggestionsNextStartIndex = createSelector(getBasketBuilder, basketBuilder =>
  isSuggestionsSuccessfullyLoaded(basketBuilder) ? basketBuilder.start + basketBuilder.size : 0,
);

export const getSuggestionsLength = createSelector(
  getSuggestions,
  suggestions => suggestions.length,
);

export const getScoreThresholdCount = createSelector(getBasketBuilder, basketBuilder =>
  isSuggestionsSuccessfullyLoaded(basketBuilder) ? basketBuilder.scoreThresholdCount : 0,
);

export const getRegulars = createSelector(
  [getSuggestions, getScoreThresholdCount],
  (suggestions, scoreThresholdCount) => suggestions.slice(0, scoreThresholdCount),
);

export const getRegularsLength = createSelector([getRegulars], regulars => regulars.length);

export const getSelectMoreRegulars = createSelector(
  [getSuggestions, getScoreThresholdCount],
  (suggestions, scoreThresholdCount) => suggestions.slice(scoreThresholdCount),
);

export const getHasSelectMore = createSelector(
  [getSuggestionsLength, getScoreThresholdCount],
  (suggestionsLength, scoreThresholdCount) => scoreThresholdCount < suggestionsLength,
);
